import { formatDate } from "../format"
import { AttributeSort } from "../sort"
import { groupBy } from "../object"

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const DAYS = []
for (let i = 1; i <= 30; i++) {
  DAYS.push(i.toString())
}

DAYS.sort()

const LOWERED_MONTHS = {}
for (const m of MONTHS.sort()) {
  LOWERED_MONTHS[m.toLowerCase()] = m
}

export function getPredictiveDate(value, dateOffset = 0) {
  const today = new Date()
  if (dateOffset == null) {
    dateOffset = 0
  }

  if (value == null || typeof value !== "string" || !value.length) {
    const date = new Date(today)
    date.setDate(date.getDate() + dateOffset)

    const text = formatDate(date)

    return {
      text,
      predictive: text,
      month: MONTHS[date.getMonth()],
      day: date.getDate(),
      year: date.getFullYear(),
    }
  }

  const currentMonth = today.getMonth()
  const currentDay = today.getDate()
  const currentYear = today.getFullYear()

  const components = value
    .split(" ")
    .map(v => v.toLowerCase())
    .map(v => v.trim())

  let month = MONTHS[currentMonth]
  let day = currentDay
  let year = currentYear

  if (components.length > 3) return null

  if (components.length >= 1) {
    const monthTest = getMonth(components[0])
    if (!monthTest) return null
    month = monthTest

    if (components.length === 1) {
      day = 1
    }
  }

  if (components.length >= 2) {
    if (components[1].endsWith(",") || components.length > 2) {
      const dayTest = Number.parseInt(components[1].replace(",", ""))
      if (Number.isNaN(dayTest)) {
        return null
      }

      day = dayTest
    } else {
      const dayTest = getDay(components[1])
      if (!dayTest) return null
      day = dayTest
    }
  }

  if (components.length === 3) {
    const yearTest = getYear(components[2], currentYear)
    if (!yearTest) return null
    year = yearTest
  }

  let date
  try {
    date = new Date(Date.parse(`${month}-${day}-${year}`))
    date.setDate(date.getDate() + dateOffset)
  } catch (e) {
    console.error(e)
    return null
  }

  const text = formatDate(date)
  const isDirect = value.toLowerCase() === text.toLowerCase()

  return {
    text,
    predictive: isDirect ? null : text.slice(value.length),
    month,
    day,
    year,
  }
}

function getMonth(value) {
  const result = Object.keys(LOWERED_MONTHS).filter(m => m.startsWith(value))
  return result.length ? LOWERED_MONTHS[result[0]] : null
}

function getDay(value) {
  if (typeof value === "number") {
    value = value.toString()
  }

  value = value.trim()
  if (!value.length) {
    return 1
  }

  const result = DAYS.filter(d => d.startsWith(value))
  if (!result.length) {
    return null
  }

  if (result.length === 1) {
    return Number.parseInt(value)
  }

  return Number.parseInt(result[1])
}

function getYear(value, defaultValue) {
  const defaultString = defaultValue.toString()

  if (typeof value === "number") {
    value = value.toString()
  }

  if (!value.length) {
    return defaultValue
  }

  if (value.length > 4) {
    return null
  }

  const year = Number.parseInt(value)
  if (Number.isNaN(year)) {
    return null
  }

  if (value.length === 4) {
    return year
  }

  if (defaultString.startsWith(value)) {
    return defaultValue
  }

  const current = Number.parseInt(defaultString.slice(0, value.length))
  if (current > year) {
    for (let y = value.length; y < 4; y++) {
      value += "9"
    }
  } else {
    for (let y = value.length; y < 4; y++) {
      value += "0"
    }
  }

  return Number.parseInt(value)
}

export const Days = [
  { label: "Sunday", shortName: "Sun", abbr: "Su", value: 7 },
  { label: "Monday", shortName: "Mon", abbr: "M", value: 1 },
  { label: "Tuesday", shortName: "Tue", abbr: "T", value: 2 },
  { label: "Wednesday", shortName: "Wed", abbr: "W", value: 3 },
  { label: "Thursday", shortName: "Thu", abbr: "Th", value: 4 },
  { label: "Friday", shortName: "Fri", abbr: "F", value: 5 },
  { label: "Saturday", shortName: "Sat", abbr: "Sa", value: 6 },
]

/**
 * Finds a day object from the Days array by JavaScript day of week value
 * @param {number} value - JavaScript day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @returns {number|null} Days value (1=Monday, 2=Tuesday, ..., 7=Sunday) or null if not found
 */
export const convertJsDayToDaysValue = value => {
  // Convert JS day of week to Days convention: 0 (Sunday) -> 7, 1-6 stay the same
  const daysValue = value === 0 ? 7 : value
  const foundDay = Days.find(day => day.value === daysValue)
  return foundDay ? foundDay.value : null
}

/**
 * Calculates the offset in days to add to a UTC date to get to the next occurrence of the specified day of week
 * @param {Date} utcDate - The UTC date to calculate from
 * @param {number} value - Days value (1=Monday, 2=Tuesday, ..., 7=Sunday)
 * @returns {number|null} Number of days to add to get to the next occurrence of the specified day, or null if invalid input
 */
export const convertDaysValueToJsDayOffset = (utcDate, value) => {
  if (!utcDate) {
    return 0
  }

  if (!(utcDate instanceof Date) || isNaN(utcDate.getTime())) {
    throw new Error(`Expected utcDate to be a valid date`)
  }

  // Convert Days convention to JS day of week: 7 (Sunday) -> 0, 1-6 stay the same
  let targetJsDay
  if (value === 7) {
    targetJsDay = 0
  } else if (value >= 1 && value <= 6) {
    targetJsDay = value
  } else {
    return null
  }

  // Get current day of week from UTC date (0=Sunday, 1=Monday, ..., 6=Saturday)
  const currentJsDay = utcDate.getUTCDay()

  // Calculate days to add to get to next occurrence of target day
  return (targetJsDay - currentJsDay + 7) % 7
}

export const WEEKDAY_DATA = {
  MONDAY: {
    label: "Monday",
    shortName: "Mon",
    value: 2,
    next: 3,
    ordinal: 0,
  },
  TUESDAY: {
    label: "Tuesday",
    shortName: "Tue",
    value: 3,
    next: 4,
    ordinal: 1,
  },
  WEDNESDAY: {
    label: "Wednesday",
    shortName: "Wed",
    value: 4,
    next: 5,
    ordinal: 2,
  },
  THURSDAY: {
    label: "Thursday",
    shortName: "Thu",
    value: 5,
    next: 6,
    ordinal: 3,
  },
  FRIDAY: {
    label: "Friday",
    shortName: "Fri",
    value: 6,
    next: 7,
    ordinal: 4,
  },
  SATURDAY: {
    label: "Saturday",
    shortName: "Sat",
    value: 7,
    next: 1,
    ordinal: 5,
  },
  SUNDAY: {
    label: "Sunday",
    shortName: "Sun",
    value: 1,
    next: 2,
    ordinal: 6,
  },
}

export const convertMinutesToRawTime = value => {
  if (value == null || value < 0 || value > 1440) {
    return null
  }

  let hours = Math.floor(value / 60)
  const minutes = Math.floor(value % 60)

  if (hours === 24) {
    hours = 0
  }

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
}

export const convertTimeToMinutes = value => {
  if (value == null || value.length < 3 || !value.includes(":")) {
    return null
  }

  return value
    .split(":")
    .map(v => Number.parseInt(v))
    .map((v, i) => {
      if (i === 0) {
        return v * 60
      }

      return v
    })
    .reduce((a, b) => a + b, 0)
}

export const DAY_OF_WEEK_TO_WEEKDAY_LOOKUP = {}
for (const day of Object.values(WEEKDAY_DATA)) {
  DAY_OF_WEEK_TO_WEEKDAY_LOOKUP[day.value] = day
}

export const convertHourToTime = hour => {
  if (hour == null) {
    return null
  }

  if (hour < 0) {
    throw new Error("Hour must be greater than 0")
  }

  if (hour > 23) {
    throw new Error("Hour must be less than 23")
  }

  if (!(typeof hour === "number")) {
    throw new Error("Hour must be a number")
  }

  const meridiem = hour < 12 ? "am" : "pm"
  if (hour === 0) {
    hour = 12
  } else if (hour > 12) {
    hour -= 12
  }

  return `${hour}${meridiem}`
}

export const convertMinutesToTime = minutes => {
  if (minutes == null) {
    return null
  }

  if (minutes < 0) {
    throw new Error("Minutes must be greater than or equal to 0")
  }

  if (minutes > 1440) {
    throw new Error("Minutes must be less than or equal to 1440")
  }

  if (!(typeof minutes === "number")) {
    throw new Error("Minutes must be a number")
  }

  if (!minutes || minutes === 1440) return "12:00am"

  let H = Math.floor(minutes / 60)
  const m = H < 12 ? "am" : "pm"

  if (H === 0) {
    H = 12
  }

  if (H > 12) {
    H -= 12
  }

  const M = minutes % 60

  return `${H}:${M.toString().padStart(2, "0")}${m}`
}

/**
 * Converts operational hours data into a display-friendly format.
 * Groups consecutive days with the same operating hours into ranges and formats them
 * with human-readable labels (e.g., "Mon - Fri 9:00am - 5:00pm").
 *
 * @param {Array<Object>} hours - Array of operational hours objects
 * @param {number} hours[].day_of_week - Day of week (1=Sunday, 7=Saturday)
 * @param {number} hours[].start_time - Start time in minutes from midnight
 * @param {number} hours[].end_time - End time in minutes from midnight
 * @returns {Array<Object>} Array of display-formatted operational hours
 * @returns {string} returns[].label - Human-readable label (e.g., "Mon - Fri 9:00am - 5:00pm")
 * @returns {Object} returns[].value - Structured data for the operational hours
 * @returns {Array<string>} returns[].value.days - Array of day abbreviations
 * @returns {number} returns[].value.start_time - Start time in minutes from midnight
 * @returns {number} returns[].value.end_time - End time in minutes from midnight
 */
export const convertOperationalHoursToDisplay = hours => {
  const convert = ([time, operationalHours]) => {
    const days = Array.from(
      new Set(operationalHours.map(o => DAY_OF_WEEK_TO_WEEKDAY_LOOKUP[o.day_of_week]))
    ).sort(AttributeSort("ordinal", "start_time"))
    const groupings = []

    if (days.length) {
      const minTime = operationalHours[0].start_time
      const maxTime = operationalHours[0].end_time

      let current = {
        ordinal: days[0].ordinal,
        days: new Set([days[0].value]),
        start_time: minTime,
        end_time: maxTime,
        data: [days[0]],
      }

      groupings.push(current)

      for (let i = 1; i < days.length; i++) {
        const previousDay = days[i - 1]
        const currentDay = days[i]

        if (previousDay.ordinal + 1 === currentDay.ordinal) {
          current.days.add(currentDay.value)
          current.data.push(currentDay)
        } else {
          current = {
            ordinal: currentDay.ordinal,
            days: new Set([currentDay.value]),
            start_time: minTime,
            end_time: maxTime,
            data: [currentDay],
          }
          groupings.push(current)
        }
      }
    }

    const formatGrouping = grouping => {
      if (!grouping.data.length) {
        return null
      }

      const first = grouping.data[0].shortName
      let day
      if (grouping.data.length === 1) {
        day = first
      } else {
        day = `${first} - ${grouping.data[grouping.data.length - 1].shortName}`
      }

      return {
        ordinal: grouping.ordinal,
        days: Array.from(grouping.days).sort(),
        start_time: grouping.start_time,
        end_time: grouping.end_time,
        data: `${day} ${time}`,
      }
    }

    return groupings.map(formatGrouping)
  }

  return Object.entries(
    groupBy(
      hours,
      h => `${convertMinutesToTime(h.start_time)} - ${convertMinutesToTime(h.end_time)}`
    )
  )
    .flatMap(convert)
    .sort(AttributeSort("ordinal", "start_time"))
    .map(v => ({
      label: v.data,
      value: { days: v.days, start_time: v.start_time, end_time: v.end_time },
    }))
}

/**
 * Converts a date string to a school timezone and returns the UTC equivalent
 * @param {string|null} dateString - Date string in format "YYYY-MM-DD", or null to use current date
 * @param {Object} options - Configuration options
 * @param {string} options.schoolTimezone - School timezone (e.g., "America/New_York" or "US/Eastern")
 * @param {number} [options.hour=0] - Hour to use (0-23), defaults to 0 (midnight)
 * @param {number} [options.minute=0] - Minute to use (0-59), defaults to 0
 * @returns {Date|null} UTC Date object representing the equivalent time in the school timezone
 *
 * @example
 * // Get UTC time for "2025-01-06" in school timezone
 * const result = getSchoolTimeInUTC("2025-01-06", {schoolTimezone: "America/New_York"})
 *
 * // Get UTC time for current date in school timezone
 * const result = getSchoolTimeInUTC(null, {schoolTimezone: "America/New_York", hour: 9, minute: 30})
 */
export const getSchoolTimeInUTC = (dateString, options) => {
  if (!options || !options.schoolTimezone) {
    return null
  }

  const { schoolTimezone, hour = 0, minute = 0 } = options

  try {
    let year, month, day

    if (dateString) {
      // Parse the provided date string
      const parts = dateString.split("-").map(Number)
      if (parts.length !== 3) {
        return null
      }
      ;[year, month, day] = parts
      if (!year || !month || !day) {
        return null
      }
    } else {
      // Use current date if dateString is null
      const today = new Date()
      year = today.getFullYear()
      month = today.getMonth() + 1
      day = today.getDate()
    }

    // Step 1: Create a UTC Date that represents the given time in the local timezone
    const localUTC = createUTCFromTimezone(
      year,
      month,
      day,
      hour,
      minute,
      Intl.DateTimeFormat().resolvedOptions().timeZone
    )
    if (!localUTC) {
      return null
    }

    // Step 2: Get the timezone offset difference
    const localOffset = getTimezoneOffsetMinutes(
      localUTC,
      Intl.DateTimeFormat().resolvedOptions().timeZone
    )
    const schoolOffset = getTimezoneOffsetMinutes(localUTC, schoolTimezone)

    if (localOffset === null || schoolOffset === null) {
      return null
    }

    // Step 3: Apply the offset difference to get the equivalent time in school timezone
    const offsetDiffMinutes = schoolOffset - localOffset
    return new Date(localUTC.getTime() + offsetDiffMinutes * 60 * 1000)
  } catch (error) {
    console.error("Error converting date between timezones:", error)
    return null
  }
}

/**
 * Creates a UTC Date object that when displayed in the given timezone shows the specified local time
 */
const createUTCFromTimezone = (year, month, day, hour, minute, timezone) => {
  try {
    // Create the target time string
    const timeString = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`

    // Use a more direct approach with Date constructor and timezone conversion
    // Start with a rough estimate
    let testDate = new Date(`${timeString}Z`) // Start with UTC

    // Iteratively adjust to find the UTC time that gives us the desired local time
    for (let i = 0; i < 10; i++) {
      const localTime = new Intl.DateTimeFormat("sv-SE", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(testDate)

      const [localDate, localTimeStr] = localTime.split(" ")
      const [localYear, localMonth, localDay] = localDate.split("-").map(Number)
      const [localHour, localMinute] = localTimeStr.split(":").map(Number)

      // Check if we have the correct local time
      if (
        localYear === year &&
        localMonth === month &&
        localDay === day &&
        localHour === hour &&
        localMinute === minute
      ) {
        return testDate
      }

      // Calculate adjustment needed
      const yearDiff = year - localYear
      const monthDiff = month - localMonth
      const dayDiff = day - localDay
      const hourDiff = hour - localHour
      const minuteDiff = minute - localMinute

      // Apply adjustments
      testDate = new Date(
        testDate.getTime() +
          yearDiff * 365 * 24 * 60 * 60 * 1000 +
          monthDiff * 30 * 24 * 60 * 60 * 1000 +
          dayDiff * 24 * 60 * 60 * 1000 +
          hourDiff * 60 * 60 * 1000 +
          minuteDiff * 60 * 1000
      )
    }

    return testDate
  } catch (error) {
    return null
  }
}

/**
 * Get the timezone offset in minutes for a given UTC date and timezone
 */
export const getTimezoneOffsetMinutes = (utcDate, timezone) => {
  try {
    // Get the local time in the specified timezone
    const localTime = new Intl.DateTimeFormat("sv-SE", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(utcDate)

    // Get the UTC time
    const utcTime = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(utcDate)

    // Parse both times
    const parseTime = timeStr => {
      const [date, time] = timeStr.split(" ")
      const [year, month, day] = date.split("-").map(Number)
      const [hour, minute] = time.split(":").map(Number)
      return new Date(year, month - 1, day, hour, minute)
    }

    const localDateTime = parseTime(localTime)
    const utcDateTime = parseTime(utcTime)

    // Calculate offset in minutes
    return (localDateTime.getTime() - utcDateTime.getTime()) / (1000 * 60)
  } catch (error) {
    return null
  }
}

/**
 * Gets the previous Monday from a UTC date (using native JavaScript Date conventions)
 * @param {Date} utcDate - The UTC date to find the previous Monday for
 * @returns {Date|null} UTC date object representing the previous Monday, or null if invalid input
 *
 * @example
 * const utcDate = getSchoolTimeInUTC("2025-01-06", {schoolTimezone: "America/New_York"})
 * const previousMonday = getPreviousMonday(utcDate)
 */
export const getPreviousMonday = utcDate => {
  if (!utcDate || !(utcDate instanceof Date) || isNaN(utcDate.getTime())) {
    return null
  }

  try {
    // Get current day of week in JavaScript UTC (0=Sunday, 1=Monday, ..., 6=Saturday)
    const currentJsDay = utcDate.getUTCDay()

    // Calculate days to subtract to get to Monday using native JavaScript conventions
    // Monday (1) returns same day, others go back to previous Monday
    // Sunday (0) goes back 6 days, Tuesday (2) goes back 1 day, etc.
    const daysToSubtract = currentJsDay === 0 ? 6 : currentJsDay - 1

    // Create a copy of the UTC date to avoid mutating the original parameter
    const mondayDate = new Date(utcDate)
    mondayDate.setUTCDate(mondayDate.getUTCDate() - daysToSubtract)

    return mondayDate
  } catch (error) {
    console.error("Error getting previous Monday:", error)
    return null
  }
}

/**
 * Adds time to a UTC date
 * @param {Date} utcDatetime - The UTC date to add time to
 * @param {Object} [options={}] - Time to add
 * @param {number} [options.days=0] - Number of days to add (can be negative to subtract)
 * @param {number} [options.hours=0] - Number of hours to add (can be negative to subtract)
 * @param {number} [options.minutes=0] - Number of minutes to add (can be negative to subtract)
 * @returns {Date|null} New UTC date object with time added, or null if invalid input
 *
 * @example
 * const monday = getPreviousMonday(utcDate)
 * const tuesday = addTime(monday, {days: 1})                    // Add 1 day
 * const mondayAt9AM = addTime(monday, {hours: 9})               // Add 9 hours
 * const wednesdayAt2PM = addTime(monday, {days: 2, hours: 14})  // Add 2 days and 14 hours
 * const withMinutes = addTime(monday, {hours: 9, minutes: 30})  // Add 9 hours and 30 minutes
 */
export const addTime = (utcDatetime, options = {}) => {
  if (!utcDatetime || !(utcDatetime instanceof Date) || isNaN(utcDatetime.getTime())) {
    return null
  }

  const { days = 0, hours = 0, minutes = 0 } = options

  if (typeof days !== "number" || typeof hours !== "number" || typeof minutes !== "number") {
    return null
  }

  try {
    // Create a copy of the UTC date to avoid mutating the original parameter
    const newDate = new Date(utcDatetime)

    // Add days
    if (days !== 0) {
      newDate.setUTCDate(newDate.getUTCDate() + days)
    }

    // Add hours
    if (hours !== 0) {
      newDate.setUTCHours(newDate.getUTCHours() + hours)
    }

    // Add minutes
    if (minutes !== 0) {
      newDate.setUTCMinutes(newDate.getUTCMinutes() + minutes)
    }

    return newDate
  } catch (error) {
    console.error("Error adding time:", error)
    return null
  }
}
